import type { SalesforceReportSectionFragment } from "../generated/graphql";
import {
  SalesforceReportAggregate,
  SalesforceReportFieldType,
  SalesforceReportFormat,
  SalesforceReportGranularity,
  SalesforceReportLinkKind,
} from "../generated/graphql";
import { crmListPath, crmRecordPath } from "./crm";
import { BLANK_CATEGORY_LABEL, formatCount } from "./salesforceDashboard";
import { formatReportValue } from "./salesforceReport";

export type ReportPage = Omit<
  SalesforceReportSectionFragment,
  | "__typename"
  | "blockKey"
  | "unavailable"
  | "missingFields"
  | "pendingObjects"
  | "chartUnavailable"
  | "chartMissingFields"
  | "chartPendingObjects"
  | "showValues"
  | "sortLegendValues"
>;
type ReportSummary = ReportPage["summaries"][number];
type ReportGroup = ReportPage["groups"][number];
type ReportGroupValue = ReportGroup["down"][number];
type ReportFilter = ReportPage["filters"][number];
type ReportColumn = ReportPage["columns"][number];
type ReportGrouping = ReportPage["down"][number];

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type ReportCell =
  | { kind: "empty" }
  | { kind: "text"; text: string; align?: "right" }
  | { kind: "link"; text: string; to: string }
  | { kind: "boolean"; checked: boolean }
  | { kind: "lines"; lines: string[] };

export type ReportRowKind = "group" | "detail" | "subtotal" | "total";

export type ReportTableRow = { key: string; kind: ReportRowKind; cells: ReportCell[] };

// A header links where a group of its grouping links to; see groupLinkTarget.
export type ReportHeaderCell = { key: string; text: string; colSpan: number; rowSpan: number; to?: string };

export type ReportTable = { headers: ReportHeaderCell[][]; rows: ReportTableRow[] };

// The texts a table needs; the page fills them from its translations.
export type ReportTableLabels = {
  field: (field: string) => string;
  // The measure's full name, as the dashboards label it.
  summary: (summary: ReportSummary) => string;
  // The short name written before a value that shares a cell with others.
  summaryPrefix: (summary: ReportSummary) => string;
  subtotal: string;
  // Null when the row counts are hidden.
  grandTotal: (count: number | null) => string;
};

export type ReportTableOptions = {
  rowCounts: boolean;
  detailRows: boolean;
  subtotals: boolean;
  grandTotal: boolean;
  recordLinks?: boolean;
};

// The address of this report narrowed to a detail cell's value, for the cells whose link is a report filter.
export type ReportFilterPath = (field: string, value: string) => string | null;

const OWNER_FILTER_KEY = "owner";
const ALL_LIST_VIEW = "all";

const EMPTY_CELL: ReportCell = { kind: "empty" };
const COUNT_SUMMARY: ReportSummary = { aggregate: SalesforceReportAggregate.Count, field: null, formula: null };
const MAX_FRACTION_DIGITS = 2;
const ISO_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T/;
const RELATIVE_NAME_PATTERN = /^[A-Z_]+$/;
const LAST_DAYS_PATTERN = /^LAST (\d+) DAYS$/;
const NO_COUNT_FORMAT_AGGREGATES: readonly SalesforceReportAggregate[] = [
  SalesforceReportAggregate.Count,
  SalesforceReportAggregate.Unique,
];

const numberFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: MAX_FRACTION_DIGITS });

const dateTimeFormat = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: "Europe/Berlin",
});

export const formatReportNumber = (value: number): string => numberFormat.format(value);

// A day as dd.MM.yyyy; any other text stays as it is.
export const formatReportDay = (value: string): string => {
  const match = ISO_DAY_PATTERN.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormat.format(date);
};

// A detail value written as its column's type says; empty is "-".
export const formatReportCell = (value: string | null, type: SalesforceReportFieldType): ReportCell => {
  if (value === null || value === "") return { kind: "text", text: BLANK_CATEGORY_LABEL };
  switch (type) {
    case SalesforceReportFieldType.Date:
      return { kind: "text", text: formatReportDay(value) };
    case SalesforceReportFieldType.DateTime:
      return { kind: "text", text: formatDateTime(value) };
    case SalesforceReportFieldType.Number: {
      const number = Number(value);
      return { kind: "text", text: Number.isFinite(number) ? formatReportNumber(number) : value, align: "right" };
    }
    case SalesforceReportFieldType.Boolean:
      return { kind: "boolean", checked: value === "true" };
    case SalesforceReportFieldType.Picklist:
      return { kind: "text", text: value.replace(/_/g, " ") };
    case SalesforceReportFieldType.Text:
      return { kind: "text", text: value };
  }
};

// Where a detail cell links to: its record, its owner's list, or this report narrowed to its value; null where the
// column has no link or the cell has nothing to link to.
const linkTarget = (
  column: Pick<ReportColumn, "field" | "link">,
  linkValue: string | null | undefined,
  filterPath: ReportFilterPath,
  recordLinks = true
) => {
  const { link } = column;
  if (!link || linkValue === null || linkValue === undefined || linkValue === "") return null;
  switch (link.kind) {
    case SalesforceReportLinkKind.Record:
      return recordLinks && link.object ? crmRecordPath(link.object, linkValue) : null;
    case SalesforceReportLinkKind.OwnerList:
      return recordLinks && link.object
        ? crmListPath(link.object, ALL_LIST_VIEW, [{ key: OWNER_FILTER_KEY, value: linkValue }])
        : null;
    case SalesforceReportLinkKind.ReportFilter:
      return filterPath(column.field, linkValue);
  }
};

export const formatSummaryValue = (summary: ReportSummary, value: number | null): string => {
  if (value === null) return BLANK_CATEGORY_LABEL;
  return NO_COUNT_FORMAT_AGGREGATES.includes(summary.aggregate) ? formatCount(value) : formatReportNumber(value);
};

const groupText = (value: ReportGroupValue, granularity: SalesforceReportGranularity | null | undefined): string =>
  value.label ?? formatReportValue(value.value, granularity);

const valuesKey = (values: ReadonlyArray<{ value?: string | null }>): string =>
  JSON.stringify(values.map(({ value }) => value ?? null));

const textCell = (text: string, align?: "right"): ReportCell => ({ kind: "text", text, align });

const withCount = (text: string, count: number, options: ReportTableOptions): string =>
  options.rowCounts ? `${text} (${formatCount(count)})` : text;

// Where a group's header links to: its owner's list or this report narrowed to the group's value, which is the
// name. A record's group is by name as well, so it never links to the record; null where there is no link or the
// group's value is blank.
const groupLinkTarget = (
  grouping: ReportGrouping | undefined,
  value: ReportGroupValue | undefined,
  filterPath: ReportFilterPath,
  recordLinks = true
): string | null =>
  grouping?.link?.kind === SalesforceReportLinkKind.Record
    ? null
    : linkTarget({ field: grouping?.field ?? "", link: grouping?.link }, value?.value, filterPath, recordLinks);

// The cell of a group's header, with the row count when shown.
const groupCell = (
  page: ReportPage,
  group: ReportGroup,
  level: number,
  options: ReportTableOptions,
  filterPath: ReportFilterPath
): ReportCell => {
  const text = withCount(groupText(group.down[level], page.down[level]?.granularity), group.count, options);
  const to = groupLinkTarget(page.down[level], group.down[level], filterPath, options.recordLinks);
  return to ? { kind: "link", text, to } : textCell(text);
};

const header = (key: string, text: string, colSpan = 1, rowSpan = 1, to?: string): ReportHeaderCell => ({
  key,
  text,
  colSpan,
  rowSpan,
  to,
});

const summaryTable = (
  page: ReportPage,
  labels: ReportTableLabels,
  options: ReportTableOptions,
  filterPath: ReportFilterPath
): ReportTable => {
  const depth = page.down.length;
  // A report without groupings still has one column for the total's label.
  const labelColumns = Math.max(depth, 1);
  const columns = options.detailRows ? page.columns : [];
  const columnIndex = new Map<string, number>();
  columns.forEach(({ field }, index) => {
    if (!columnIndex.has(field)) columnIndex.set(field, index);
  });
  const placement = page.summaries.map(summary =>
    summary.aggregate !== SalesforceReportAggregate.Formula && summary.field
      ? columnIndex.get(summary.field)
      : undefined
  );
  const hasTotals = options.subtotals || options.grandTotal;
  const extraSummaries = hasTotals
    ? page.summaries.flatMap((summary, index) => (placement[index] === undefined ? [{ summary, index }] : []))
    : [];
  const columnCount = labelColumns + columns.length + extraSummaries.length;

  const blankRow = (): ReportCell[] => Array.from({ length: columnCount }, () => EMPTY_CELL);

  const totalCells = (group: ReportGroup, labelCell: ReportCell, labelIndex: number): ReportCell[] => {
    const cells = blankRow();
    cells[labelIndex] = labelCell;
    const lines = new Map<number, string[]>();
    page.summaries.forEach((summary, index) => {
      const text = formatSummaryValue(summary, group.values[index] ?? null);
      const target = placement[index];
      if (target !== undefined && hasTotals) {
        lines.set(target, [...(lines.get(target) ?? []), `${labels.summaryPrefix(summary)}: ${text}`]);
      }
    });
    lines.forEach((entries, target) => {
      cells[labelColumns + target] = { kind: "lines", lines: entries };
    });
    extraSummaries.forEach(({ summary, index }, extraIndex) => {
      cells[labelColumns + columns.length + extraIndex] = textCell(
        formatSummaryValue(summary, group.values[index] ?? null),
        "right"
      );
    });
    return cells;
  };

  const groups = page.groups.filter(group => group.down.length > 0 && group.across.length === 0);
  const grand = page.groups.find(group => group.down.length === 0 && group.across.length === 0);
  const groupKeys = new Set(groups.map(group => valuesKey(group.down)));
  const rowsOf = new Map<string, ReportPage["rows"]>();
  const looseRows: ReportPage["rows"] = [];
  page.rows.forEach(row => {
    let owner: string | undefined;
    for (let length = row.down.length; length > 0 && owner === undefined; length -= 1) {
      const key = valuesKey(row.down.slice(0, length));
      if (groupKeys.has(key)) owner = key;
    }
    if (owner === undefined) {
      looseRows.push(row);
    } else {
      const owned = rowsOf.get(owner);
      if (owned) owned.push(row);
      else rowsOf.set(owner, [row]);
    }
  });

  const rows: ReportTableRow[] = [];
  const detailRow = (key: string, row: ReportPage["rows"][number]): ReportTableRow => {
    const cells = blankRow();
    columns.forEach((column, index) => {
      const cell = formatReportCell(row.values[index] ?? null, column.type);
      const to = cell.kind === "text" ? linkTarget(column, row.links[index], filterPath, options.recordLinks) : null;
      cells[labelColumns + index] = to && cell.kind === "text" ? { kind: "link", text: cell.text, to } : cell;
    });
    return { key, kind: "detail", cells };
  };

  const open: ReportGroup[] = [];
  const closeGroup = (group: ReportGroup) => {
    if (!options.subtotals) return;
    rows.push({
      key: `subtotal:${valuesKey(group.down)}`,
      kind: "subtotal",
      cells: totalCells(group, textCell(labels.subtotal), group.down.length - 1),
    });
  };
  groups.forEach(group => {
    while (open.length > 0 && open[open.length - 1].down.length >= group.down.length) {
      closeGroup(open.pop() as ReportGroup);
    }
    const key = valuesKey(group.down);
    const cells = blankRow();
    const level = group.down.length - 1;
    cells[level] = groupCell(page, group, level, options, filterPath);
    rows.push({ key: `group:${key}`, kind: "group", cells });
    open.push(group);
    if (options.detailRows) {
      (rowsOf.get(key) ?? []).forEach((row, index) => rows.push(detailRow(`row:${key}:${index}`, row)));
    }
  });
  while (open.length > 0) closeGroup(open.pop() as ReportGroup);
  if (options.detailRows) looseRows.forEach((row, index) => rows.push(detailRow(`row:${index}`, row)));
  if (options.grandTotal && grand) {
    rows.push({
      key: "total",
      kind: "total",
      cells: totalCells(grand, textCell(labels.grandTotal(options.rowCounts ? grand.count : null)), 0),
    });
  }

  return {
    headers: [
      [
        ...(depth === 0
          ? [header("label", "")]
          : page.down.map(({ field }, index) => header(`down:${index}`, labels.field(field)))),
        ...columns.map(({ field }, index) => header(`column:${index}`, labels.field(field))),
        ...extraSummaries.map(({ summary, index }) => header(`summary:${index}`, labels.summary(summary))),
      ],
    ],
    rows,
  };
};

// What a matrix cell lists, in the order of the measure column: the record count (unless hidden), then the summaries.
const showsCount = (page: ReportPage, options: ReportTableOptions): boolean =>
  options.rowCounts || page.summaries.length === 0;

const matrixCell = (page: ReportPage, group: ReportGroup | undefined, options: ReportTableOptions): ReportCell => {
  if (!group) return EMPTY_CELL;
  return {
    kind: "lines",
    lines: [
      ...(showsCount(page, options) ? [formatCount(group.count)] : []),
      ...page.summaries.map((summary, index) => formatSummaryValue(summary, group.values[index] ?? null)),
    ],
  };
};

type MatrixColumn = { kind: "leaf" | "subtotal"; across: ReportGroupValue[] };

// The columns across in order: every innermost group, and after the innermost groups of an outer group that
// group's subtotal (when shown).
const matrixColumns = (leaves: ReportGroup[], depth: number, subtotals: boolean): MatrixColumn[] => {
  const columns: MatrixColumn[] = [];
  const open = new Map<number, ReportGroupValue[]>();
  const closeFrom = (level: number, nextKey: (length: number) => string | undefined) => {
    for (let length = depth - 1; length >= level; length -= 1) {
      const prefix = open.get(length);
      if (prefix && valuesKey(prefix) !== nextKey(length)) {
        if (subtotals) columns.push({ kind: "subtotal", across: prefix });
        open.delete(length);
      }
    }
  };
  leaves.forEach(leaf => {
    closeFrom(1, length => valuesKey(leaf.across.slice(0, length)));
    for (let length = 1; length < depth; length += 1) {
      if (!open.has(length)) open.set(length, leaf.across.slice(0, length));
    }
    columns.push({ kind: "leaf", across: leaf.across });
  });
  closeFrom(1, () => undefined);
  return columns;
};

const matrixTable = (
  page: ReportPage,
  labels: ReportTableLabels,
  options: ReportTableOptions,
  filterPath: ReportFilterPath
): ReportTable => {
  const depth = page.down.length;
  const acrossDepth = page.across.length;
  const labelColumns = Math.max(depth, 1);
  const leaves =
    acrossDepth === 0
      ? []
      : page.groups.filter(group => group.down.length === 0 && group.across.length === acrossDepth);
  const columns = matrixColumns(leaves, acrossDepth, options.subtotals);
  const byKey = new Map(page.groups.map(group => [`${valuesKey(group.down)}|${valuesKey(group.across)}`, group]));
  const dataCells = (down: ReportGroupValue[]): ReportCell[] => [
    ...columns.map(column => matrixCell(page, byKey.get(`${valuesKey(down)}|${valuesKey(column.across)}`), options)),
    ...(options.grandTotal ? [matrixCell(page, byKey.get(`${valuesKey(down)}|${valuesKey([])}`), options)] : []),
  ];
  // One column after the groupings down names what each cell's lines are, as Salesforce stacks them.
  const measureCell: ReportCell = {
    kind: "lines",
    lines: [
      ...(showsCount(page, options) ? [labels.summary(COUNT_SUMMARY)] : []),
      ...page.summaries.map(summary => labels.summary(summary)),
    ],
  };
  const emptyData = (): ReportCell[] =>
    Array.from({ length: columns.length + (options.grandTotal ? 1 : 0) }, () => EMPTY_CELL);
  const blankLabels = (): ReportCell[] => Array.from({ length: labelColumns }, () => EMPTY_CELL);

  const rows: ReportTableRow[] = [];
  const open: ReportGroup[] = [];
  const closeGroup = (group: ReportGroup) => {
    if (!options.subtotals) return;
    const cells = blankLabels();
    cells[group.down.length - 1] = textCell(labels.subtotal);
    rows.push({
      key: `subtotal:${valuesKey(group.down)}`,
      kind: "subtotal",
      cells: [...cells, measureCell, ...dataCells(group.down)],
    });
  };
  page.groups
    .filter(group => group.down.length > 0 && group.across.length === 0)
    .forEach(group => {
      while (open.length > 0 && open[open.length - 1].down.length >= group.down.length) {
        closeGroup(open.pop() as ReportGroup);
      }
      const level = group.down.length - 1;
      const cells = blankLabels();
      cells[level] = groupCell(page, group, level, options, filterPath);
      const isLeaf = group.down.length === depth;
      rows.push({
        key: `group:${valuesKey(group.down)}`,
        kind: "group",
        cells: [...cells, ...(isLeaf ? [measureCell, ...dataCells(group.down)] : [EMPTY_CELL, ...emptyData()])],
      });
      if (!isLeaf) open.push(group);
    });
  while (open.length > 0) closeGroup(open.pop() as ReportGroup);
  const grand = page.groups.find(group => group.down.length === 0 && group.across.length === 0);
  if (options.grandTotal && grand) {
    const cells = blankLabels();
    cells[0] = textCell(labels.grandTotal(options.rowCounts ? grand.count : null));
    rows.push({ key: "total", kind: "total", cells: [...cells, measureCell, ...dataCells([])] });
  }

  const totalHeader = options.grandTotal ? [header("total", labels.grandTotal(null), 1, Math.max(acrossDepth, 1))] : [];
  const downHeaders =
    depth === 0
      ? [header("label", "")]
      : page.down.map(({ field }, index) => header(`down:${index}`, labels.field(field)));
  if (acrossDepth === 0) {
    return { headers: [[...downHeaders, header("measure", ""), ...totalHeader]], rows };
  }
  const headers: ReportHeaderCell[][] = [];
  for (let level = 1; level <= acrossDepth; level += 1) {
    const cells: ReportHeaderCell[] = [];
    columns.forEach(column => {
      const columnDepth = column.kind === "leaf" ? acrossDepth : column.across.length;
      // A subtotal's header spans the rows below its own.
      if (columnDepth < level) return;
      if (column.kind === "subtotal" && columnDepth === level) {
        cells.push(header(`subtotal:${valuesKey(column.across)}`, labels.subtotal, 1, acrossDepth - level + 1));
        return;
      }
      const key = valuesKey(column.across.slice(0, level));
      const previous = cells[cells.length - 1];
      if (previous?.key === key) {
        previous.colSpan += 1;
      } else {
        const value = column.across[level - 1];
        cells.push(
          header(
            key,
            groupText(value, page.across[level - 1]?.granularity),
            1,
            1,
            groupLinkTarget(page.across[level - 1], value, filterPath, options.recordLinks) ?? undefined
          )
        );
      }
    });
    headers.push([
      ...(level === acrossDepth
        ? [...downHeaders, header("measure", "")]
        : [header(`corner:${level}`, "", labelColumns + 1)]),
      ...cells,
      ...(level === 1 ? totalHeader : []),
    ]);
  }
  return { headers, rows };
};

export const buildReportTable = (
  page: ReportPage,
  labels: ReportTableLabels,
  options: ReportTableOptions,
  filterPath: ReportFilterPath
): ReportTable =>
  page.format === SalesforceReportFormat.Matrix
    ? matrixTable(page, labels, options, filterPath)
    : summaryTable(page, labels, options, filterPath);

export const defaultReportTableOptions = (page: ReportPage): ReportTableOptions => ({
  rowCounts: true,
  detailRows: page.columns.length > 0,
  subtotals: page.showSubtotals,
  grandTotal: page.showGrandTotal,
});

const fieldLabel = (translate: Translate, page: ReportPage, field: string): string =>
  translate(`salesforceReports.fields.${page.source}.${field}`, { _: field });

const summaryLabel = (translate: Translate, page: ReportPage, summary: ReportSummary): string => {
  if (summary.aggregate === SalesforceReportAggregate.Formula) {
    return translate(`salesforceReports.formulas.${summary.formula ?? ""}`, { _: summary.formula ?? "" });
  }
  return translate(`salesforceReports.measures.${summary.aggregate}`, {
    field: summary.field ? fieldLabel(translate, page, summary.field) : "",
  });
};

const summaryPrefix = (translate: Translate, page: ReportPage, summary: ReportSummary): string => {
  switch (summary.aggregate) {
    case SalesforceReportAggregate.Sum:
      return translate("salesforceReports.report.sum");
    case SalesforceReportAggregate.Average:
      return translate("salesforceReports.report.average");
    case SalesforceReportAggregate.Unique:
      return translate("salesforceReports.report.unique");
    case SalesforceReportAggregate.Count:
    case SalesforceReportAggregate.Formula:
      return summaryLabel(translate, page, summary);
  }
};

export const reportTableLabels = (translate: Translate, page: ReportPage): ReportTableLabels => ({
  field: field => fieldLabel(translate, page, field),
  summary: summary => summaryLabel(translate, page, summary),
  summaryPrefix: summary => summaryPrefix(translate, page, summary),
  subtotal: translate("salesforceReports.report.subtotal"),
  grandTotal: count =>
    count === null
      ? translate("salesforceReports.report.grandTotalWithoutCount")
      : translate("salesforceReports.report.grandTotal", { count: formatCount(count) }),
});

const filterValue = (translate: Translate, value: string): string => {
  if (value === "") return '""';
  if (ISO_DAY_PATTERN.test(value)) return formatReportDay(value);
  if (ISO_TIME_PATTERN.test(value)) return formatDateTime(value);
  const lastDays = LAST_DAYS_PATTERN.exec(value);
  if (lastDays) return translate("salesforceReports.ranges.lastDays", { days: lastDays[1] });
  return RELATIVE_NAME_PATTERN.test(value) ? translate(`salesforceReports.ranges.${value}`, { _: value }) : value;
};

const filterText = (translate: Translate, page: ReportPage, filter: ReportFilter): string =>
  [
    fieldLabel(translate, page, filter.field),
    translate(`salesforceReports.operators.${filter.operator}`),
    filter.values.map(value => filterValue(translate, value)).join(", "),
  ]
    .filter(part => part !== "")
    .join(" ");

export type ReportFilterDescription = {
  dateRange: string | null;
  // Numbered as the report's filter logic refers to them.
  filters: string[];
  filterLogic: string | null;
  linked: string[];
};

export const describeReportFilters = (translate: Translate, page: ReportPage): ReportFilterDescription => {
  const { dateRange } = page;
  let dateRangeText: string | null = null;
  if (dateRange) {
    const name = dateRange.relative
      ? translate(`salesforceReports.ranges.${dateRange.relative}`)
      : dateRange.lastDays !== null
        ? translate("salesforceReports.ranges.lastDays", { days: dateRange.lastDays })
        : null;
    const period =
      dateRange.from || dateRange.to
        ? `${dateRange.from ? formatReportDay(dateRange.from) : ""} – ${dateRange.to ? formatReportDay(dateRange.to) : ""}`.trim()
        : null;
    const parts = [name, period && name ? `(${period})` : period].filter((part): part is string => !!part);
    dateRangeText = parts.length > 0 ? `${fieldLabel(translate, page, dateRange.field)}: ${parts.join(" ")}` : null;
  }
  return {
    dateRange: dateRangeText,
    filters: page.filters.map((filter, index) => `${index + 1}. ${filterText(translate, page, filter)}`),
    filterLogic: page.filterLogic
      ? translate("salesforceReports.report.filterLogic", { logic: page.filterLogic })
      : null,
    linked: page.linkedFilters.map(filter => filterText(translate, page, filter)),
  };
};
